import { useState } from 'react';

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: 'Can I get help setting up ReceptionGenie?',
      answer: 'Definitely! We take care of the whole setup for you. We\'ll train your AI, add your FAQs, customize your responses, and make sure everything is running perfectly.'
    },
    {
      question: 'How long does it take to set up?',
      answer: 'Setup is simple. We can activate your core features within a few hours, and your full customized configuration will be completed within 48 hours. That includes website training, FAQs, call handling preferences, and final tuning.'
    },
    {
      question: 'Does ReceptionGenie stay updated on my business information?',
      answer: 'Yes! ReceptionGenie automatically pulls your business hours and other info from your Google Business Profile and website. You can also edit and update details anytime through your dashboard, and the changes take effect immediately.'
    },
    {
      question: 'How can I provide ReceptionGenie feedback?',
      answer: 'You can always update and add FAQs for ReceptionGenie in the Admin dashboard. If you need assistance or want to make adjustments to how Genie handles calls, just reach out to our 24/7 support team and we\'ll guide you through the updates.'
    },
    {
      question: 'Can I keep my phone number?',
      answer: 'Yes! You don\'t need to change a thing. We\'ll guide you through setting up call forwarding with your existing phone number. Your customers will continue calling the same number they always have.'
    },
    {
      question: 'What happens if ReceptionGenie can\'t answer a question?',
      answer: 'If ReceptionGenie encounters a question it can\'t answer, it will politely let the caller know and offer to transfer them to you or take a message. You\'ll receive an instant notification so you can follow up quickly.'
    },
    {
      question: 'Can I cancel anytime?',
      answer: 'Yes, you can cancel your subscription at any time with no penalties or hidden fees. We also offer a 30-day money-back guarantee if you\'re not completely satisfied with the service.'
    },
    {
      question: 'Does ReceptionGenie work with my calendar?',
      answer: 'Yes! ReceptionGenie integrates seamlessly with Google Calendar, Outlook, and other popular calendar applications. It will only show available time slots based on your actual schedule and automatically add confirmed appointments.'
    }
  ];

  return (
    <section id="faq" className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-600">
            Everything you need to know about ReceptionGenie
          </p>
        </div>
        
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={index}
              className="bg-white border-2 border-gray-200 rounded-2xl overflow-hidden hover:border-pink-300 transition-colors duration-300"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer hover:bg-gray-50 transition-colors duration-300"
              >
                <h3 className="text-lg font-bold text-gray-900 pr-4">{faq.question}</h3>
                <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 bg-pink-100 rounded-full transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-pink-600 text-xl"></i>
                </div>
              </button>
              
              <div 
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-6 pb-5">
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-12 bg-gradient-to-r from-pink-50 to-purple-50 rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-3">
            Still have questions?
          </h3>
          <p className="text-gray-600 mb-6">
            Our team is here to help 24/7. Get in touch and we'll answer any questions you have.
          </p>
          <a 
            href="mailto:support@receptiongenie.ai"
            className="inline-block whitespace-nowrap px-8 py-3 bg-pink-500 hover:bg-pink-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer"
          >
            Contact Support
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;